import { parse } from "acorn";

/**
 * Static auth-guard detection for serverless functions.
 *
 * A function is "protected" when an auth signal (isInServiceRole,
 * User.me() result, or a "admin" role literal) is used in an IfStatement
 * whose consequent short-circuits (return/throw). Mere reference is not
 * enough — `console.log(isInServiceRole)` does not gate anything.
 */

function visit(node, fn) {
    if (!node || typeof node !== "object") return;
    fn(node);
    for (const key of Object.keys(node)) {
        const child = node[key];
        if (Array.isArray(child)) {
            for (const c of child) visit(c, fn);
        } else if (child && typeof child === "object" && child.type) {
            visit(child, fn);
        }
    }
}

function isUserMeCall(node) {
    return (
        node?.type === "CallExpression" &&
        node.callee?.type === "MemberExpression" &&
        node.callee.object?.name === "User" &&
        node.callee.property?.name === "me"
    );
}

function collectUserMeVars(ast) {
    const vars = new Set();
    visit(ast, (node) => {
        if (node.type !== "VariableDeclarator") return;
        let init = node.init;
        if (init?.type === "AwaitExpression") init = init.argument;
        if (isUserMeCall(init) && node.id?.type === "Identifier") {
            vars.add(node.id.name);
        }
    });
    return vars;
}

function testReferencesGuard(testNode, userMeVars) {
    let hit = false;
    visit(testNode, (n) => {
        if (n.type === "Identifier" && n.name === "isInServiceRole") hit = true;
        if (n.type === "Identifier" && userMeVars.has(n.name)) hit = true;
        if (isUserMeCall(n)) hit = true;
        if (n.type === "Literal" && n.value === "admin") hit = true;
    });
    return hit;
}

function bodyShortCircuits(node) {
    let hit = false;
    visit(node, (n) => {
        if (n.type === "ReturnStatement" || n.type === "ThrowStatement") {
            hit = true;
        }
    });
    return hit;
}

function anyGuardReference(ast, userMeVars) {
    let hit = false;
    visit(ast, (n) => {
        if (n.type === "Identifier" && n.name === "isInServiceRole") hit = true;
        if (n.type === "Identifier" && userMeVars.has(n.name)) hit = true;
        if (isUserMeCall(n)) hit = true;
    });
    return hit;
}

export function analyzeFunctionAuth(code) {
    if (typeof code !== "string" || code.trim() === "") {
        return { passed: false, reason: "noCode" };
    }

    let ast;
    try {
        ast = parse(code, {
            ecmaVersion: "latest",
            sourceType: "module",
            allowAwaitOutsideFunction: true,
            allowReturnOutsideFunction: true,
        });
    } catch {
        // Parse failure (e.g. TS syntax) — fall back to a regex sniff so a
        // valid-but-unparseable file still gets a best-effort signal.
        const stripped = code
            .replace(/\/\*[\s\S]*?\*\//g, "")
            .replace(/\/\/[^\n]*/g, "");
        const ref =
            /\bisInServiceRole\b|\bUser\s*\.\s*me\s*\(|["'`]admin["'`]/.test(
                stripped
            );
        return ref
            ? { passed: true, reason: "regexFallback" }
            : { passed: false, reason: "noGuard" };
    }

    const userMeVars = collectUserMeVars(ast);

    let enforced = false;
    visit(ast, (node) => {
        if (node.type !== "IfStatement") return;
        if (
            testReferencesGuard(node.test, userMeVars) &&
            bodyShortCircuits(node.consequent)
        ) {
            enforced = true;
        }
    });

    if (enforced) return { passed: true, reason: "enforcedGuard" };
    if (anyGuardReference(ast, userMeVars)) {
        return { passed: false, reason: "referencedNotEnforced" };
    }
    return { passed: false, reason: "noGuard" };
}
